export type NavbarLink = {
  href: string;
  label: string;
  variant?: "highlight" | "highlight_two" | "secondary" | "contact";
  subDrawerId?: string;
  showArrow?: boolean;
};

export type NavbarAction = {
  key: "account" | "bookmarks" | string;
  label: string;
  variant: "account" | "bookmark" | string;
};

export type SubDrawerItem = {
  label: string;
  href: string;
  subItems: { label: string; href: string; subDrawerId?: string }[];
  subDrawerId?: string; // optional nested subdrawer to open when this item is clicked (mobile)
};

export type SubDrawer = {
  id: string;
  title: string;
  items: SubDrawerItem[];
  ctas: { id: string; label: string; href: string; className?: string; variant?: string }[];
  isAccount?: boolean;
  bgColor?: string; // optional custom background color for the subdrawer
};

export const NAVBAR_LINKS: NavbarLink[] = [
  { href: "/formations", label: "Formations", subDrawerId: "formations", variant: "highlight", showArrow: true },
  { href: "#type-formation", label: "Type de formation", subDrawerId: "type-formation", variant: "highlight", showArrow: true },
  { href: "#coaching", label: "Coaching", subDrawerId: "coaching", variant: "highlight", showArrow: true },
  { href: "/entrepreneurship", label: "Entreprenariat", variant: "highlight" },
  { href: "/apropos", label: "À propos", variant: "highlight" },
  { href: "/methode", label: "Méthode", variant: "highlight" },
  // { href: "/rejoindre", label: "Nous rejoindre", variant: "highlight_two" },
  // { href: "/enseigner", label: "Enseigner chez nous", variant: "secondary" },
  { href: "/actualites", label: "Actualités", variant: "highlight" },
  { href: "/contact", label: "Nous contacter", variant: "contact" },
];

export const NAVBAR_ACTIONS: NavbarAction[] = [
  { key: "account", label: "Accès à votre Compte", variant: "account" },
  { key: "bookmarks", label: "Mes Bookmarks", variant: "bookmark" },
];

export const SUB_DRAWERS: Record<string, SubDrawer> = {
  formations: {
    id: "formations",
    title: "Formations",
    items: [
      {
        label: "Marketing et Digital",
        href: "#formations-marketing",
        subDrawerId: "marketing-digital",
        subItems: [
          { label: "Copywriting", href: "#copywriting" },
          { label: "WordPress", href: "#wordpress" },
          { label: "E-commerce", href: "#ecommerce" },
          { label: "SEO", href: "#seo" },
          { label: "Google Ads", href: "#google-ads" },
          { label: "Social Ads", href: "#social-ads" },
        ],
      },
      {
        label: "Développement Commercial et Entrepreneuriat",
        href: "#formations-business",
        subDrawerId: "business-entrepreneurship",
        subItems: [
          { label: "Vente et Négociation", href: "#vente" },
          { label: "Gestion de Projet", href: "#gestion" },
          { label: "Business Plan", href: "#bp" },
          { label: "Leadership", href: "#leadership" },
        ],
      },
      {
        label: "Réseaux Sociaux et Contenus",
        href: "#formations-social",
        subDrawerId: "social-media",
        subItems: [
          { label: "Instagram & TikTok", href: "#insta-tiktok" },
          { label: "Content Marketing", href: "#content" },
          { label: "Community Management", href: "#community" },
          { label: "Montage Vidéo", href: "#video" },
        ],
      },
      {
        label: "Outils et Productivité",
        href: "#formations-outils",
        subDrawerId: "tools-productivity",
        subItems: [
          { label: "IA Générative", href: "#ia" },
          { label: "No-Code", href: "#nocode" },
          { label: "Pack Office", href: "#office" },
          { label: "Trello & Notion", href: "#tools" },
        ],
      },
      {
        label: "Développement Personnel et Leadership",
        href: "#formations-leadership",
        subDrawerId: "personal-development",
        subItems: [
          { label: "Prise de parole", href: "#parole" },
          { label: "Gestion du stress", href: "#stress" },
          { label: "Intelligence émotionnelle", href: "#ie" },
          { label: "Confiance en soi", href: "#confiance" },
        ],
      },
      {
        label: "Accessibles aux personnes en situation d'handicap",
        href: "#formations-accessibles",
        subDrawerId: "handicap-accessible",
        subItems: [
          { label: "Formations adaptées", href: "#adaptee" },
          { label: "Accompagnement PMR", href: "#pmr" },
          { label: "Inclusion numérique", href: "#inclusion" },
        ],
      },
    ],
    ctas: [
      {
        id: "all-trainings",
        label: "Découvrir toutes nos formations",
        href: "#formations",
        className: "hero__btn-primary",
        variant: "primary",
      },
      {
        id: "financement",
        label: "Être accompagné dans mon financement",
        href: "#financement",
        className: "btn--ghost",
        variant: "ghost",
      },
    ],
  },
  'type-formation': {
    id: 'type-formation',
    title: 'Type de formation',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Formation Intra', href: '#formation-intra', subItems: [] },
      { label: 'Formation Inter', href: '#formation-inter', subItems: [] },
      { label: 'Formation Sur-Mesure', href: '#formation-sur-mesure', subItems: [] },
    ],
    ctas: [
      { id: 'tf-all', label: 'Voir toutes les formations', href: '#tf-all' },
      { id: 'tf-contact', label: 'Contacter un conseiller', href: '#tf-contact' },
    ],
  },
  coaching: {
    id: 'coaching',
    title: 'Coaching',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Coaching professionnel', href: '/coaching-professionnel', subItems: [] },
      { label: "Coaching d'équipe", href: '/coaching-equipe', subItems: [] },
      { label: 'Coaching Manager', href: '/coaching-manager', subItems: [] },
    ],
    ctas: [
      { id: 'coaching-all', label: 'Découvrir nos coachings', href: '#coaching-all' },
      { id: 'coaching-contact', label: 'Contacter un conseiller', href: '#coaching-contact' },
    ],
  },
  account: {
    id: "account",
    title: "Accès à votre Compte",
    isAccount: true,
    items: [],
    ctas: [],
  },
  'marketing-digital': {
    id: 'marketing-digital',
    title: 'Marketing Digital',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Marketing Digital', href: '#marketing-digital', subItems: [] },
      { label: 'Copywriting', href: '#copywriting', subItems: [] },
      { label: 'WordPress', href: '#wordpress', subItems: [] },
      { label: 'E-commerce', href: '#ecommerce', subItems: [] },
      { label: 'SEO', href: '#seo', subItems: [] },
      { label: 'Google Ads', href: '#google-ads', subItems: [] },
      { label: 'Social Ads', href: '#social-ads', subItems: [] },
    ],
    ctas: [
      { id: 'md-all', label: 'Voir toutes les formations MD', href: '#md-all' },
      { id: 'md-contact', label: 'Contacter un conseiller', href: '#md-contact' },
    ],
  },
  'business-entrepreneurship': {
    id: 'business-entrepreneurship',
    title: 'Business & Entrepreneuriat',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Vente et Négociation', href: '#vente', subItems: [] },
      { label: 'Gestion de Projet', href: '#gestion', subItems: [] },
      { label: 'Business Plan', href: '#bp', subItems: [] },
      { label: 'Leadership', href: '#leadership', subItems: [] },
    ],
    ctas: [
      { id: 'be-all', label: 'Voir toutes les formations Business', href: '#be-all' },
      { id: 'be-contact', label: 'Contacter un conseiller', href: '#be-contact' },
    ],
  },
  'social-media': {
    id: 'social-media',
    title: 'Réseaux Sociaux',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Instagram & TikTok', href: '#insta-tiktok', subItems: [] },
      { label: 'Content Marketing', href: '#content', subItems: [] },
      { label: 'Community Management', href: '#community', subItems: [] },
      { label: 'Montage Vidéo', href: '#video', subItems: [] },
    ],
    ctas: [
      { id: 'sm-all', label: 'Voir toutes les formations Social', href: '#sm-all' },
      { id: 'sm-contact', label: 'Contacter un conseiller', href: '#sm-contact' },
    ],
  },
  'tools-productivity': {
    id: 'tools-productivity',
    title: 'Outils & Productivité',
    bgColor: '#C7F8FF',
    items: [
      { label: 'IA Générative', href: '#ia', subItems: [] },
      { label: 'No-Code', href: '#nocode', subItems: [] },
      { label: 'Pack Office', href: '#office', subItems: [] },
      { label: 'Trello & Notion', href: '#tools', subItems: [] },
    ],
    ctas: [
      { id: 'tp-all', label: 'Voir toutes les formations Outils', href: '#tp-all' },
      { id: 'tp-contact', label: 'Contacter un conseiller', href: '#tp-contact' },
    ],
  },
  'personal-development': {
    id: 'personal-development',
    title: 'Développement Personnel',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Prise de parole', href: '#parole', subItems: [] },
      { label: 'Gestion du stress', href: '#stress', subItems: [] },
      { label: 'Intelligence émotionnelle', href: '#ie', subItems: [] },
      { label: 'Confiance en soi', href: '#confiance', subItems: [] },
    ],
    ctas: [
      { id: 'pd-all', label: 'Voir toutes les formations DP', href: '#pd-all' },
      { id: 'pd-contact', label: 'Contacter un conseiller', href: '#pd-contact' },
    ],
  },
  'handicap-accessible': {
    id: 'handicap-accessible',
    title: 'Accessibilité',
    bgColor: '#C7F8FF',
    items: [
      { label: 'Formations adaptées', href: '#adaptee', subItems: [] },
      { label: 'Accompagnement PMR', href: '#pmr', subItems: [] },
      { label: 'Inclusion numérique', href: '#inclusion', subItems: [] },
    ],
    ctas: [
      { id: 'ha-all', label: 'Voir toutes les formations', href: '#ha-all' },
      { id: 'ha-contact', label: 'Contacter un conseiller', href: '#ha-contact' },
    ],
  },
};
